import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';
import DonutChart from '@/components/charts/DonutChart';
import type { DonutChartDataTypes } from '@/components/charts/DonutChart/DonutChart.types';
import HorizontalBarChart from '@/components/charts/HorizontalBarChart';
import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget';
import SmallCard from '@/components/SmallCard';
import { BLUE_SCALE_COLORS } from '@/constants/chartcolors.constants';
import { YEAR_MONTH_DAY_TIME_FORMAT } from '@/constants/date.constants';
import { LOG_PRODUCTS, PRODUCTS } from '@/constants/products.constants';
import {
  type ProductDTO,
  selectedOrganizationAtom
} from '@/pages/Organizations';
import { interpolateColorHex } from '@/pages/ReportsTemplates/MDR/ReportsTemplate.utils';
import dashboardTimeFrameAtom, {
  casesEventsByDateAtom,
  casesPerDayAtom
} from '../Dashboard.store';
import {
  CASES_BAR_LINE_SERIES,
  CASES_BAR_SERIES
} from '../dashboard.constants';
import useDashboard from '../useDashboard.hooks';
import { useFetchCaseMetrics } from './DashboardCases.store';
import useStyles from './DashboardCases.styles';

const DashboardCases = () => {
  const [casesPerDay, setCasesPerDay] = useAtom(casesPerDayAtom);
  const casesEventsData = useAtomValue(casesEventsByDateAtom);
  const { orgProducts } = useDashboard();
  const dashboardTimeFrame = useAtomValue(dashboardTimeFrameAtom);
  const selectedOrganization = useAtomValue(selectedOrganizationAtom);

  const fromDate = dayjs()
    .subtract(dashboardTimeFrame + 1, 'day')
    .startOf('day')
    .format(YEAR_MONTH_DAY_TIME_FORMAT);

  const toDate = dayjs()
    .subtract(1, 'day')
    .endOf('day')
    .format(YEAR_MONTH_DAY_TIME_FORMAT);

  const {
    data: casesByCaseTitleData,
    isFetching: casesByCaseTitleDataIsFetching
  } = useFetchCaseMetrics({
    created_at: { from_date: fromDate, to_date: toDate },
    group_by: 'case_title'
  });

  const { data: casesBySensorData, isFetching: casesBySensorDataIsFetching } =
    useFetchCaseMetrics({
      created_at: { from_date: fromDate, to_date: toDate },
      group_by: 'host'
    });

  const { data: casesBySourceData, isFetching: casesBySourceIsFetching } =
    useFetchCaseMetrics({
      created_at: { from_date: fromDate, to_date: toDate },
      group_by: 'source'
    });

  const { data: cases, isFetching: isFetchingCases } = useFetchCaseMetrics(
    { created_at: { from_date: fromDate, to_date: toDate }, group_by: 'day' },
    setCasesPerDay
  );

  const totalNetworkCases =
    cases
      ?.map((item) => item.network_count)
      .reduce((acc, item) => acc + (item || 0), 0) || 0;

  const totalLogCases =
    cases
      ?.map((item) => item.log_count)
      .reduce((acc, item) => acc + (item || 0), 0) || 0;

  const totalCases = useMemo(() => {
    return casesPerDay?.reduce((acc, item) => acc + (item.count || 0), 0);
  }, [casesPerDay]);

  const hasNetwork = useMemo(
    () => orgProducts.some((f: ProductDTO) => f.product_type === PRODUCTS.NET),
    [orgProducts]
  );

  const hasLog = useMemo(
    () =>
      orgProducts.some((f: ProductDTO) =>
        LOG_PRODUCTS.includes(f.product_type as PRODUCTS)
      ),
    [orgProducts]
  );

  const { classes } = useStyles({
    hasNetwork,
    hasLog,
    selectedOrganizationLabel: selectedOrganization.label
  });

  const casesBySource = useMemo(() => {
    const renamedData =
      casesBySourceData?.map((item) => ({
        name: item.value,
        count: item.count
      })) || [];

    const sortedTop = renamedData.sort((a, b) => b.count - a.count).slice(0, 5);

    return sortedTop.map((item, index, array) => ({
      ...item,
      color: interpolateColorHex(
        BLUE_SCALE_COLORS.LIGHT,
        BLUE_SCALE_COLORS.DARK,
        index / Math.max(1, array.length - 1)
      )
    }));
  }, [casesBySourceData]);

  const casesByCaseTitle = useMemo(() => {
    if (!casesByCaseTitleData) return [];
    return casesByCaseTitleData.slice(0, 10).map((c) => ({
      name: c.value,
      count: c.count
    }));
  }, [casesByCaseTitleData]);

  const casesBySensor = useMemo(() => {
    if (!casesBySensorData) return [];
    return casesBySensorData.slice(0, 10).map((c) => ({
      name: c.value,
      count: c.count
    }));
  }, [casesBySensorData]);

  return (
    <div className={classes.dashboardPage}>
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Total Cases"
          primaryContent={
            <div className={classes.smallCardText}>
              {totalCases.toLocaleString()}
            </div>
          }
        />
      </div>
      {((hasNetwork && hasLog) || selectedOrganization.label === 'all') && (
        <>
          <div className={classes.dashboardSmallItem}>
            <SmallCard
              title="Total Network Cases"
              primaryContent={
                <div className={classes.smallCardText}>
                  {totalNetworkCases.toLocaleString()}
                </div>
              }
            />
          </div>
          <div className={classes.dashboardSmallItem}>
            <SmallCard
              title="Total Log Cases"
              primaryContent={
                <div className={classes.smallCardText}>
                  {totalLogCases.toLocaleString()}
                </div>
              }
            />
          </div>
        </>
      )}
      <div className={classes.dashboardSmallItem}>
        <SmallCard
          title="Avg. Cases Per Day"
          primaryContent={
            <div className={classes.smallCardText}>
              {casesEventsData[0].averageCases || 0}
            </div>
          }
        />
      </div>
      <div className={classes.dashboardFullWidthItem}>
        <GenericChartWidget title="Cases Per Day" loading={isFetchingCases}>
          <VerticalBarChart
            data={casesEventsData}
            series={CASES_BAR_LINE_SERIES as AgBarSeriesOptions[]}
            suppressLegend
          />
        </GenericChartWidget>
      </div>
      <div className={classes.dashboardMediumItem}>
        <GenericChartWidget
          title="Top Case Titles"
          loading={casesByCaseTitleDataIsFetching}
        >
          <HorizontalBarChart
            data={casesByCaseTitle}
            series={CASES_BAR_SERIES}
          />
        </GenericChartWidget>
      </div>
      {
        <div className={classes.dashboardMediumItem}>
          <GenericChartWidget
            title="Top Cases by Source"
            loading={casesBySourceIsFetching}
          >
            <DonutChart
              data={casesBySource as DonutChartDataTypes[]}
              legendPosition="right"
              resolveCssVariables
            />
          </GenericChartWidget>
        </div>
      }
      {
        <div className={classes.dashboardMediumItem}>
          <GenericChartWidget
            title="Top Case Hosts"
            loading={casesBySensorDataIsFetching}
          >
            <HorizontalBarChart
              data={casesBySensor}
              series={CASES_BAR_SERIES}
            />
          </GenericChartWidget>
        </div>
      }
    </div>
  );
};

export default DashboardCases;
